import { PortfolioDatesData, PortfolioMilestone, PortfolioPhaseDates } from "../../../../../models";
// Imported from their own files rather than the `utils` barrel: the barrel drags in the app's runtime
// module graph, where this mapper is otherwise pure and type-only.
import { getDateFromValue } from "../../../../../utils/date/date";
import { stringKeyToSentenceCase } from "../../../../../utils/string/stringCase";
import { getStageStatus } from "../../../../../utils/workflow/getStageStatus";
import { GanttPhaseBar, GanttRoadmapMilestone, GanttRoadmapRow } from "../../../../../widget";

/**
 * A deal's pipeline stages, ordered by start date.
 *
 * The order *is* the colouring: `mode="stage"` colours each bar by its position in this array, so a
 * stage is never dropped for want of an end date — legacy fell back to the start (a point-in-time
 * stage), and so does this. A stage with no start at all cannot be placed and is the only one skipped.
 * A backwards range collapses to its start rather than drawing a 4px stub at the wrong date.
 *
 * Stage keys are the deal's own field names (`firstDueDiligence`, `heads_of_terms`), labelled through
 * the same `stringKeyToSentenceCase` the tasks board and the new-deal journey use, so a stage reads
 * identically wherever it appears.
 */
const toStageBars = (dealUuid: string, dates: Record<string, PortfolioPhaseDates>): GanttPhaseBar[] =>
  Object.entries(dates)
    .reduce<{ key: string; startDate: Date; endDate: Date; status?: string }[]>((stages, [key, stage]) => {
      const startDate = getDateFromValue(stage.start);
      if (!startDate) return stages;

      const endDate = getDateFromValue(stage.end) ?? startDate;

      return [
        ...stages,
        {
          key,
          startDate,
          endDate: endDate < startDate ? startDate : endDate,
          status: getStageStatus(stage) ?? undefined,
        },
      ];
    }, [])
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .map(({ key, startDate, endDate, status }) => ({
      id: `${dealUuid}:stage:${key}`,
      label: stringKeyToSentenceCase(key),
      status,
      startDate,
      endDate,
    }));

/**
 * Milestones carry no phase key in stage mode — the chart colours a marker from the stage its date
 * falls inside. Undated milestones are dropped, since the chart positions by date.
 *
 * The milestone's own uuid is the marker id where there is one — it comes from the workflow task the
 * milestone is, so it survives a refetch and stays distinct when two milestones share a label. Only a
 * milestone with no uuid falls back to the label, which is all there is to identify it by; an array
 * index would not do, since it shifts once a milestone is added ahead of another.
 */
const toMilestones = (dealUuid: string, milestones: PortfolioMilestone[] | undefined): GanttRoadmapMilestone[] =>
  (milestones ?? []).reduce<GanttRoadmapMilestone[]>((acc, milestone) => {
    const date = getDateFromValue(milestone.date);
    if (!date) return acc;

    return [...acc, { id: milestone.uuid ?? `${dealUuid}:milestone:${milestone.label}`, label: milestone.label, date }];
  }, []);

/**
 * Portfolio roadmap page data → `GanttChart` rows (`variant="project-roadmap"`, `mode="stage"`).
 *
 * The server's ordering is kept and no deal is dropped: a deal with no placeable stage still yields a
 * row, so the roadmap keeps matching the user's deal selection rather than silently shrinking.
 *
 * `relatedProjects` is passed through even when empty — the chart then prints "No related project"
 * under the deal name, which is the state the legacy row label left blank.
 */
export const toGanttDealRows = (deals: PortfolioDatesData[]): GanttRoadmapRow[] =>
  deals.map((deal) => ({
    id: deal.uuid,
    name: deal.displayName,
    relatedProjects: deal.relatedProjects ?? [],
    phases: toStageBars(deal.uuid, deal.dates),
    milestones: toMilestones(deal.uuid, deal.milestones),
  }));
